import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  FaArrowRight, FaHospital, FaLandmark, FaIndustry, FaStore, FaCloud, FaUniversity,
} from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import TestimonialsSection from './TestimonialsSection';
import { getRevealProps, staggerContainer, staggerItem, defaultViewport } from '../motion/scrollMotion';
import './ServiceDetailPage.css';
import './CustomersPage.css';

export const caseStudies = [
  {
    sector: 'Healthcare',
    title: 'Regional Multi-Specialty Hospital Group',
    challenge: 'A hospital group running patient records and diagnostic systems across multiple facilities needed to close gaps ahead of an ISO 27001 compliance audit, after a routine review flagged weak segmentation between clinical and administrative networks.',
    approach: 'We ran a full network penetration test and configuration review across all sites, then worked with their IT team to redesign network segmentation, tighten access controls on diagnostic imaging systems, and stand up centralized log monitoring.',
    outcome: 'The group passed its compliance audit on schedule, closed 100% of critical and high findings within the agreed remediation window, and now runs quarterly vulnerability scans as part of an ongoing managed program.',
  },
  {
    sector: 'Financial Services',
    title: 'Mid-Market NBFC & Lending Platform',
    challenge: 'A non-banking financial company expanding its digital lending platform needed to demonstrate security maturity to a bank partner before an integration could go live, on a deadline measured in weeks, not months.',
    approach: 'Our team scoped a combined web application and API penetration test covering the loan origination flow, KYC document handling, and payment integration, alongside a focused review of access control between customer accounts.',
    outcome: 'We identified and helped remediate an authorization flaw in the API that would have allowed cross-account data access, delivered the final report within the partner\'s deadline, and the integration went live on schedule.',
  },
  {
    sector: 'Manufacturing & Industrial',
    title: 'Industrial Manufacturer, Multi-Site Operations',
    challenge: 'An industrial manufacturer with plant-floor OT systems and a growing corporate IT footprint had never had its industrial network formally assessed, and leadership wanted assurance ahead of an insurance renewal.',
    approach: 'We conducted an OT/IT segmentation review, tested the corporate network for lateral movement paths into plant-floor systems, and delivered a prioritized hardening roadmap that did not require production downtime to implement.',
    outcome: 'The assessment gave leadership and their insurer a clear, evidence-based view of risk, and the manufacturer implemented network segmentation changes that materially reduced the attack surface between IT and OT environments.',
  },
  {
    sector: 'Retail & D2C',
    title: 'Retail Chain with E-commerce Platform',
    challenge: 'A retail brand running both physical stores and a growing D2C e-commerce platform needed ongoing security coverage but did not have the budget or hiring runway for an in-house security team.',
    approach: 'We now run co-managed security monitoring for their e-commerce infrastructure, combined with quarterly application testing ahead of major sale events when the platform sees its highest transaction volumes.',
    outcome: 'The platform has run through three major sale periods without a security incident, with our SOC handling alert triage and escalation so their small IT team can focus on the store rollout rather than security operations.',
  },
];

const industries = [
  { icon: <FaHospital />, name: 'Healthcare', desc: 'Patient data, diagnostic systems, and compliance-ready security.', href: '/industries/healthcare' },
  { icon: <FaLandmark />, name: 'Financial Services', desc: 'Lending platforms, payments, and regulatory-grade assurance.', href: '/industries/financial-services' },
  { icon: <FaIndustry />, name: 'Manufacturing & Industrial', desc: 'Network segmentation and plant-floor risk reduction.', href: '/services/network-penetration-testing' },
  { icon: <FaStore />, name: 'Retail & D2C', desc: 'E-commerce platforms and seasonal traffic resilience.', href: '/industries/ecommerce-retail' },
  { icon: <FaCloud />, name: 'SaaS & Technology', desc: 'Multi-tenant security and compliance for growing platforms.', href: '/industries/saas-technology' },
  { icon: <FaUniversity />, name: 'Government & Public Sector', desc: 'Critical infrastructure and public-facing service security.', href: '/industries/government-public-sector' },
];

const outcomes = [
  { value: '500+', label: 'Engagements delivered', desc: 'Across cybersecurity, managed IT, and secure development.' },
  { value: '100%', label: 'Critical findings remediated', desc: 'Every critical and high-severity finding closed within the agreed window across our case study engagements.' },
  { value: '<30 min', label: 'Mean time to triage', desc: 'Median time for our SOC to triage a confirmed alert once it fires.' },
  { value: '1 business day', label: 'Response guarantee', desc: 'Every inquiry gets a substantive response within one business day.' },
];

const CustomersPage = () => (
  <div className="svc-page">
    <PageNavbar />

    <section className="svc-hero">
      <div className="svc-hero-glow" />
      <div className="svc-container">
        <motion.div className="cust-hero-text" {...getRevealProps('fadeUp')}>
          <span className="svc-eyebrow">Customers</span>
          <h1 className="svc-hero-title">How Organizations Partner With Us.</h1>
          <p className="svc-hero-desc">
            From regional hospital groups to industrial manufacturers, the organizations we work
            with span sectors with very different risk profiles — and very little tolerance for
            vague answers. Here is what those engagements actually look like.
          </p>
          <div className="svc-hero-actions">
            <Link to="/contact" className="svc-btn svc-btn-primary">
              Get In Touch <FaArrowRight />
            </Link>
            <Link to="/services" className="svc-btn svc-btn-ghost">
              Explore Services
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Case Studies */}
    <section className="svc-section">
      <div className="svc-container">
        <header className="svc-section-head">
          <span className="svc-section-tag">Case Studies</span>
          <h2 className="svc-section-title">Real Engagements, Anonymized</h2>
          <p className="svc-section-sub">
            Client names are withheld by agreement — the challenges, approach, and outcomes below
            are real.
          </p>
        </header>

        <motion.div
          className="cust-case-grid"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={{ ...staggerContainer, visible: { transition: { staggerChildren: 0.1 } } }}
        >
          {caseStudies.map((cs) => (
            <motion.article className="cust-case-card" key={cs.title} variants={staggerItem}>
              <span className="cust-case-sector">{cs.sector}</span>
              <h3 className="cust-case-title">{cs.title}</h3>
              <div className="cust-case-block">
                <span className="cust-case-label">Challenge</span>
                <p>{cs.challenge}</p>
              </div>
              <div className="cust-case-block">
                <span className="cust-case-label">Approach</span>
                <p>{cs.approach}</p>
              </div>
              <div className="cust-case-block cust-case-outcome">
                <span className="cust-case-label">Outcome</span>
                <p>{cs.outcome}</p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>

    {/* Industries Served */}
    <section className="svc-section svc-section-alt">
      <div className="svc-container">
        <header className="svc-section-head">
          <span className="svc-section-tag">Industries Served</span>
          <h2 className="svc-section-title">Tailored Security Across Every Sector</h2>
          <p className="svc-section-sub">
            The right control set for a hospital group looks nothing like the right control set
            for a lending platform. We scope engagements around what your industry actually needs.
          </p>
        </header>

        <motion.div
          className="svc-feature-grid cust-industries-grid"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={{ ...staggerContainer, visible: { transition: { staggerChildren: 0.07 } } }}
        >
          {industries.map((ind) => (
            <motion.div className="svc-feature-card" key={ind.name} variants={staggerItem}>
              <Link to={ind.href} className="cust-industry-link">
                <div className="svc-feature-icon">{ind.icon}</div>
                <h3 className="svc-feature-title">{ind.name}</h3>
                <p className="svc-feature-desc">{ind.desc}</p>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* Measurable Outcomes */}
    <section className="svc-section">
      <div className="svc-container">
        <header className="svc-section-head">
          <span className="svc-section-tag">Measurable Outcomes</span>
          <h2 className="svc-section-title">Risk Reduced, Compliance Achieved, Trust Earned</h2>
          <p className="svc-section-sub">
            We track outcomes the same way our clients do — not by how many alerts fired, but by
            what actually got fixed, and how fast.
          </p>
        </header>

        <div className="cust-outcomes-grid">
          {outcomes.map((o) => (
            <div className="cust-outcome-card" key={o.label}>
              <div className="cust-outcome-value">{o.value}</div>
              <div className="cust-outcome-label">{o.label}</div>
              <p className="cust-outcome-desc">{o.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonials (shared component) */}
    <TestimonialsSection />

    <section className="svc-cta">
      <div className="svc-container">
        <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
          <h2 className="svc-cta-title">Want to be the next case study?</h2>
          <p className="svc-cta-desc">
            Tell us what you are trying to protect — we will scope an engagement around your
            actual risk, not a generic package.
          </p>
          <div className="svc-cta-actions">
            <Link to="/contact" className="svc-btn svc-btn-primary">
              Book Consultation <FaArrowRight />
            </Link>
            <Link to="/services" className="svc-btn svc-btn-ghost-light">
              View All Services
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    <Footer />
  </div>
);

export default CustomersPage;
